import React, {
  useRef,
  useEffect,
  useState,
  useContext,
  useCallback,
  MouseEvent as ReactMouseEvent,
  useMemo,
} from 'react';
import { ThemeProvider } from 'styled-components';
import { ButtonProps } from './types';
import {
  ButtonContainer,
  ButtonText,
  SubmittingContainer,
  Button as StyledButton,
  Spinner,
} from './styles';
import { ThemeContext } from '..';
import BasicIcon from '../BasicIcon/BasicIcon';

/**
 * @param buttonColor
 * @param label
 * @param onClick
 * @param type
 * @param isSubmitting
 * @param submitSuccess
 * @param submitFailure
 * @param disabled
 * @param icon
 * @param fullWidth
 */
const Button: React.FC<ButtonProps> = (props) => {
  const {
    label,
    onClick,
    type = 'button',
    status = 'default',
    hierarchy = 'primary',
    size = 'medium',
    isSubmitting,
    disabled,
    iconName,
    iconPosition = 'left',
    fullWidth,
    testId,
    fontWeight = 'bold',
    isMobile = false,
  } = props;
  const { actualTheme, isMobile: mobileTheme } = useContext(ThemeContext);
  const useMobileVersion = useMemo(() => (isMobile !== undefined ? isMobile : mobileTheme), [
    isMobile,
    mobileTheme,
  ]);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const intervalRef = useRef<number | number>(null);
  const [loaderWidth, setLoaderWidth] = useState(0);

  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!isSubmitting && onClick) {
        event.stopPropagation();
        onClick(event);
      }
    },
    [isSubmitting, onClick],
  );

  useEffect(() => {
    if (isSubmitting) {
      intervalRef.current = window.setInterval(() => {
        setLoaderWidth((preWidth) => {
          if (preWidth > 100) return preWidth;
          return preWidth + 2;
        });
      }, 100);
    } else {
      clearInterval(intervalRef.current);
      setLoaderWidth(0);
    }
  }, [isSubmitting]);

  return (
    <ThemeProvider theme={actualTheme}>
      <StyledButton
        ref={buttonRef}
        id="button-id"
        type={type}
        onClick={handleClick}
        status={status}
        hierarchy={hierarchy}
        size={size}
        isSubmitting={isSubmitting}
        fullWidth={fullWidth}
        hasIcon={!!iconName}
        iconPosition={iconPosition}
        disabled={disabled || (isSubmitting && hierarchy !== 'minimal' && !iconName)}
        data-testid={testId}
        data-loader={iconName && isSubmitting}
        circular={iconName && !label}
        fontWeight={fontWeight}
        isMobile={useMobileVersion}
      >
        <ButtonContainer>
          {iconName && !isSubmitting && (
            <BasicIcon name={iconName} size={size} data-testid={`${testId}-icon`} />
          )}
          {iconName && isSubmitting && (
            <Spinner status={status} size={size} hierarchy={hierarchy} />
          )}
          {label && <ButtonText data-testid={`${testId}-label`}>{label}</ButtonText>}
        </ButtonContainer>
        {isSubmitting && hierarchy !== 'minimal' && !iconName && (
          <SubmittingContainer status={status} loaderWidth={loaderWidth} hasIcon={!!iconName}>
            {iconName && <BasicIcon name={iconName} size={size} />}
            {label && <ButtonText>{label}</ButtonText>}
          </SubmittingContainer>
        )}
      </StyledButton>
    </ThemeProvider>
  );
};

export default Button;
